import * as THREE from 'three';
import { World, CHUNK_HEIGHT } from './world';
import { BLOCKS } from './blocks';

const GRAVITY = -25;
const WATER_GRAVITY = -5;
const JUMP_FORCE = 9;
const SWIM_FORCE = 5;
const WALK_SPEED = 5;
const SPRINT_SPEED = 8;
const PLAYER_HEIGHT = 1.7;
const PLAYER_WIDTH = 0.3;
const WATER_BLOCK = 7;
const LAVA_BLOCK = 40;
const FLYBLOCK = 44;
const SUPERFLYBLOCK = 45;
const FLYBLOCK_FORCE = JUMP_FORCE * 4; // ~36 — launches player very high
const SUPERFLYBLOCK_FORCE = FLYBLOCK_FORCE * 3; // 3x higher than flyblock
const WATER_SPEED_MULTIPLIER = 0.5;
const LAVA_DAMAGE_RATE = 25; // oxygen drain per second when in lava
const OXYGEN_DRAIN_RATE = 15; // per second
const OXYGEN_REGEN_RATE = 30; // per second
const MAX_OXYGEN = 100;
const PORTAL_FILL = 47;
const PORTAL_COOLDOWN = 3; // seconds
const ICE_BLOCK = 30;
const ICE_FRICTION = 0.98;
const SPEED_BLOCK = 63;
const SPEED_BOOST_DURATION = 5; // seconds
const SPEED_BOOST_MULTIPLIER = 2.5;
const ARROW_BLOCK_NORTH = 55;
const ARROW_BLOCK_EAST = 56;
const ARROW_BLOCK_SOUTH = 57;
const ARROW_BLOCK_WEST = 58;
const ARROW_PUSH_SPEED = 12;
const ARROW_PUSH_DURATION = 0.4; // seconds of sustained push
const MUSIC_BLOCK = 59;
const LAUNCH_PAD = 50;
const LAUNCH_SPEED = 60;
const LAUNCH_VERTICAL = 40;
const LAUNCH_GRAVITY = -2.5;
const LAUNCH_COOLDOWN = 3; // seconds
const TELEPORT_BLOCK = 60;
const TELEPORT_COOLDOWN = 5; // seconds
const PAINT_DURATION = 30; // seconds

export class Player {
  camera: THREE.PerspectiveCamera;
  position: THREE.Vector3;
  velocity: THREE.Vector3 = new THREE.Vector3();
  onGround = false;
  yaw = 0;
  pitch = 0;
  world: World;

  keys: Record<string, boolean> = {};
  sprinting = false;

  // Water / drowning state
  inWater = false;
  oxygen = 100;
  dead = false;

  // Sleep state
  sleeping = false;

  // New block state
  speedBoostTimer = 0;
  launching = false;
  paintColor: number = 0; // 0 = no paint, 51-54 = paint block id
  paintTimer = 0;
  private iceVelocityX = 0;
  private iceVelocityZ = 0;
  private onIce = false;
  private launchCooldown = 0;
  private teleportCooldown = 0;
  private arrowPushTimer = 0;
  private arrowPushDirX = 0;
  private arrowPushDirZ = 0;
  private lastMusicBlockKey = '';

  // Sound callbacks
  onJump: (() => void) | null = null;
  onLand: (() => void) | null = null;
  onStep: (() => void) | null = null;
  onDrown: (() => void) | null = null;
  onEnterWater: (() => void) | null = null;
  onPortalEnter: ((wx: number, wy: number, wz: number) => void) | null = null;
  onSpeedBoost: (() => void) | null = null;
  onLaunch: (() => void) | null = null;
  onPaintEnter: (() => void) | null = null;
  onTeleportBlockEnter: (() => void) | null = null;
  onMusicBlockStep: ((wx: number, wy: number, wz: number) => void) | null = null;
  private portalCooldown = 0;
  private stepTimer = 0;
  private wasOnGround = false;
  private wasInWater = false;
  private stepUpOffset = 0; // camera smoothing for auto-step

  private keydownHandler: (e: KeyboardEvent) => void;
  private keyupHandler: (e: KeyboardEvent) => void;

  constructor(camera: THREE.PerspectiveCamera, world: World) {
    this.camera = camera;
    this.world = world;
    this.position = new THREE.Vector3(8, 220, 8);

    this.keydownHandler = (e) => {
      this.keys[e.code] = true;
      if (e.code === 'ShiftLeft') this.sprinting = true;
    };
    this.keyupHandler = (e) => {
      this.keys[e.code] = false;
      if (e.code === 'ShiftLeft') this.sprinting = false;
    };

    document.addEventListener('keydown', this.keydownHandler);
    document.addEventListener('keyup', this.keyupHandler);
  }

  dispose() {
    document.removeEventListener('keydown', this.keydownHandler);
    document.removeEventListener('keyup', this.keyupHandler);
  }

  onMouseMove(dx: number, dy: number) {
    const sensitivity = 0.002;
    this.yaw -= dx * sensitivity;
    this.pitch -= dy * sensitivity;
    this.pitch = Math.max(-Math.PI / 2 + 0.01, Math.min(Math.PI / 2 - 0.01, this.pitch));
  }

  private collides(x: number, y: number, z: number): boolean {
    // Check multiple points around the player's hitbox
    const offsets = [-PLAYER_WIDTH, PLAYER_WIDTH];
    for (const dx of offsets) {
      for (const dz of offsets) {
        // Check feet, knees, waist, chest, head
        for (let dy = 0; dy <= PLAYER_HEIGHT; dy += PLAYER_HEIGHT / 3) {
          const bx = Math.floor(x + dx);
          const by = Math.floor(y + dy);
          const bz = Math.floor(z + dz);
          const block = this.world.getBlock(bx, by, bz);
          if (block !== 0 && BLOCKS[block]?.solid) return true;
        }
      }
    }
    return false;
  }

  update(dt: number) {
    // Clamp dt to avoid physics issues
    dt = Math.min(dt, 0.05);

    // Dead or sleeping players don't move
    if (this.dead || this.sleeping) return;

    // Don't apply physics until the chunk under the player is loaded
    const pcx = Math.floor(this.position.x / 16);
    const pcz = Math.floor(this.position.z / 16);
    if (!this.world.getChunk(pcx, pcz)) return;

    const speed = this.sprinting ? SPRINT_SPEED : WALK_SPEED;

    // Movement direction
    const forward = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
    const right = new THREE.Vector3(Math.cos(this.yaw), 0, -Math.sin(this.yaw));

    const moveDir = new THREE.Vector3();
    if (this.keys['KeyW'] || this.keys['ArrowUp']) moveDir.add(forward);
    if (this.keys['KeyS'] || this.keys['ArrowDown']) moveDir.sub(forward);
    if (this.keys['KeyA'] || this.keys['ArrowLeft']) moveDir.sub(right);
    if (this.keys['KeyD'] || this.keys['ArrowRight']) moveDir.add(right);

    if (moveDir.length() > 0) {
      moveDir.normalize();
    }

    // Check water at feet and head positions
    const feetX = Math.floor(this.position.x);
    const feetY = Math.floor(this.position.y);
    const feetZ = Math.floor(this.position.z);
    const headY = Math.floor(this.position.y + PLAYER_HEIGHT);

    const feetBlock = this.world.getBlock(feetX, feetY, feetZ);
    const headBlock = this.world.getBlock(feetX, headY, feetZ);
    const feetInWater = feetBlock === WATER_BLOCK;
    const headInWater = headBlock === WATER_BLOCK;
    const feetInLava = feetBlock === LAVA_BLOCK;

    // Portal detection
    if (this.portalCooldown > 0) {
      this.portalCooldown -= dt;
    } else if (feetBlock === PORTAL_FILL || headBlock === PORTAL_FILL) {
      const portalBlock = feetBlock === PORTAL_FILL ? feetBlock : headBlock;
      const py = feetBlock === PORTAL_FILL ? feetY : headY;
      this.onPortalEnter?.(feetX, py, feetZ);
      this.portalCooldown = PORTAL_COOLDOWN;
    }

    // Update inWater (head submerged)
    this.inWater = headInWater;

    // Fire onEnterWater callback on transition
    if (this.inWater && !this.wasInWater) {
      this.onEnterWater?.();
    }

    // Oxygen management — lava and drowning both drain oxygen
    if (this.inWater || feetInLava) {
      const drainRate = feetInLava ? LAVA_DAMAGE_RATE : OXYGEN_DRAIN_RATE;
      this.oxygen -= drainRate * dt;
      if (this.oxygen <= 0) {
        this.oxygen = 0;
        if (!this.dead) {
          this.dead = true;
          this.onDrown?.();
        }
      }
    } else {
      this.oxygen = Math.min(MAX_OXYGEN, this.oxygen + OXYGEN_REGEN_RATE * dt);
    }

    // Paint detection (walk through non-solid paint blocks at feet)
    if (feetBlock >= 51 && feetBlock <= 54) {
      if (this.paintColor !== feetBlock) {
        this.onPaintEnter?.();
      }
      this.paintColor = feetBlock;
      this.paintTimer = PAINT_DURATION;
    }
    if (this.paintTimer > 0) {
      this.paintTimer -= dt;
      if (this.paintTimer <= 0) {
        this.paintColor = 0;
        this.paintTimer = 0;
      }
    }

    // Speed boost timer
    if (this.speedBoostTimer > 0) {
      this.speedBoostTimer -= dt;
      if (this.speedBoostTimer < 0) this.speedBoostTimer = 0;
    }

    // Launch cooldown
    if (this.launchCooldown > 0) this.launchCooldown -= dt;

    // Teleport cooldown
    if (this.teleportCooldown > 0) this.teleportCooldown -= dt;

    // Compute effective speed and apply water slowdown
    let effectiveSpeed = speed;
    if (feetInWater) {
      effectiveSpeed *= WATER_SPEED_MULTIPLIER;
    }
    if (this.speedBoostTimer > 0) {
      effectiveSpeed *= SPEED_BOOST_MULTIPLIER;
    }

    // Horizontal velocity
    this.velocity.x = moveDir.x * effectiveSpeed;
    this.velocity.z = moveDir.z * effectiveSpeed;

    // Ice sliding: add ice velocity
    const blockBelow = this.world.getBlock(
      Math.floor(this.position.x),
      Math.floor(this.position.y) - 1,
      Math.floor(this.position.z)
    );
    if (this.onGround && blockBelow === ICE_BLOCK) {
      if (!this.onIce) {
        // Just stepped onto ice
        this.iceVelocityX = this.velocity.x;
        this.iceVelocityZ = this.velocity.z;
        this.onIce = true;
      }
      // Add player input to ice velocity
      this.iceVelocityX += moveDir.x * effectiveSpeed * 0.1;
      this.iceVelocityZ += moveDir.z * effectiveSpeed * 0.1;
      // Apply ice friction
      this.iceVelocityX *= Math.pow(ICE_FRICTION, dt * 60);
      this.iceVelocityZ *= Math.pow(ICE_FRICTION, dt * 60);
      // Override velocity with ice velocity
      this.velocity.x = this.iceVelocityX;
      this.velocity.z = this.iceVelocityZ;
    } else {
      if (this.onIce) {
        this.iceVelocityX = 0;
        this.iceVelocityZ = 0;
        this.onIce = false;
      }
    }

    // Arrow block push (when standing on it — sustained push)
    if (this.onGround && blockBelow >= ARROW_BLOCK_NORTH && blockBelow <= ARROW_BLOCK_WEST) {
      this.arrowPushTimer = ARROW_PUSH_DURATION;
      switch (blockBelow) {
        case ARROW_BLOCK_NORTH: this.arrowPushDirX = 0; this.arrowPushDirZ = -1; break;
        case ARROW_BLOCK_EAST:  this.arrowPushDirX = 1; this.arrowPushDirZ = 0; break;
        case ARROW_BLOCK_SOUTH: this.arrowPushDirX = 0; this.arrowPushDirZ = 1; break;
        case ARROW_BLOCK_WEST:  this.arrowPushDirX = -1; this.arrowPushDirZ = 0; break;
      }
    }
    if (this.arrowPushTimer > 0) {
      this.arrowPushTimer -= dt;
      this.velocity.x = this.arrowPushDirX * ARROW_PUSH_SPEED;
      this.velocity.z = this.arrowPushDirZ * ARROW_PUSH_SPEED;
    }

    // Music block detection (when standing on or above it — play note once per block)
    // Also check block at feet level in case player is inside the block space
    const feetBlockId = this.world.getBlock(
      Math.floor(this.position.x),
      Math.floor(this.position.y),
      Math.floor(this.position.z)
    );
    const onMusicBlock = blockBelow === MUSIC_BLOCK || feetBlockId === MUSIC_BLOCK;
    if (onMusicBlock) {
      const bx = Math.floor(this.position.x);
      const by = blockBelow === MUSIC_BLOCK ? Math.floor(this.position.y) - 1 : Math.floor(this.position.y);
      const bz = Math.floor(this.position.z);
      const key = `${bx},${by},${bz}`;
      if (this.lastMusicBlockKey !== key) {
        this.lastMusicBlockKey = key;
        this.onMusicBlockStep?.(bx, by, bz);
      }
    } else {
      this.lastMusicBlockKey = '';
    }

    // Speed block detection (when standing on it)
    if (this.onGround && blockBelow === SPEED_BLOCK && this.speedBoostTimer <= 0) {
      this.speedBoostTimer = SPEED_BOOST_DURATION;
      this.onSpeedBoost?.();
    }

    // Launch pad detection (when standing on it)
    if (this.onGround && blockBelow === LAUNCH_PAD && this.launchCooldown <= 0) {
      const fwd = new THREE.Vector3(-Math.sin(this.yaw), 0, -Math.cos(this.yaw));
      this.velocity.x = fwd.x * LAUNCH_SPEED;
      this.velocity.z = fwd.z * LAUNCH_SPEED;
      this.velocity.y = LAUNCH_VERTICAL;
      this.onGround = false;
      this.launching = true;
      this.launchCooldown = LAUNCH_COOLDOWN;
      this.onLaunch?.();
    }

    // Teleport block detection (when standing on it)
    if (this.onGround && blockBelow === TELEPORT_BLOCK && this.teleportCooldown <= 0) {
      this.teleportCooldown = TELEPORT_COOLDOWN;
      this.onTeleportBlockEnter?.();
    }

    // Gravity - reduced in water, reduced when launching
    if (feetInWater) {
      this.velocity.y += WATER_GRAVITY * dt;
    } else if (this.launching) {
      this.velocity.y += LAUNCH_GRAVITY * dt;
    } else {
      this.velocity.y += GRAVITY * dt;
    }

    // Jump / swim
    if (this.keys['Space']) {
      if (feetInWater) {
        // Swimming up - works regardless of onGround
        this.velocity.y = SWIM_FORCE;
      } else if (this.onGround) {
        this.velocity.y = JUMP_FORCE;
        this.onGround = false;
        this.onJump?.();
      }
    }

    // Apply movement with collision detection (separated axes + auto-step)
    const newX = this.position.x + this.velocity.x * dt;
    if (!this.collides(newX, this.position.y, this.position.z)) {
      this.position.x = newX;
    } else if (this.onGround && !this.collides(newX, this.position.y + 1, this.position.z)) {
      // Auto-step up 1 block
      this.position.x = newX;
      this.position.y += 1;
      this.stepUpOffset = -1;
    } else {
      this.velocity.x = 0;
    }

    const newZ = this.position.z + this.velocity.z * dt;
    if (!this.collides(this.position.x, this.position.y, newZ)) {
      this.position.z = newZ;
    } else if (this.onGround && !this.collides(this.position.x, this.position.y + 1, newZ)) {
      // Auto-step up 1 block
      this.position.z = newZ;
      this.position.y += 1;
      this.stepUpOffset = -1;
    } else {
      this.velocity.z = 0;
    }

    const newY = this.position.y + this.velocity.y * dt;
    if (!this.collides(this.position.x, newY, this.position.z)) {
      this.position.y = newY;
      this.onGround = false;
    } else {
      if (this.velocity.y < 0) {
        if (!this.wasOnGround) this.onLand?.();
        this.onGround = true;
        this.launching = false;

        // Flyblock trampoline: bounce when landing on it
        const landingBlock = this.world.getBlock(
          Math.floor(this.position.x),
          Math.floor(this.position.y) - 1,
          Math.floor(this.position.z)
        );
        if (landingBlock === FLYBLOCK || landingBlock === SUPERFLYBLOCK) {
          this.velocity.y = landingBlock === SUPERFLYBLOCK ? SUPERFLYBLOCK_FORCE : FLYBLOCK_FORCE;
          this.onGround = false;
          this.onJump?.();
        }
      }
      if (this.velocity.y <= 0) this.velocity.y = 0;
    }

    // Footstep sounds
    if (this.onGround && moveDir.length() > 0) {
      const stepInterval = this.sprinting ? 0.25 : 0.4;
      this.stepTimer += dt;
      if (this.stepTimer >= stepInterval) {
        this.stepTimer = 0;
        this.onStep?.();
      }
    } else {
      this.stepTimer = 0;
    }

    this.wasOnGround = this.onGround;
    this.wasInWater = this.inWater;

    // Prevent falling below world
    if (this.position.y < 0) {
      this.position.y = 220;
      this.velocity.y = 0;
    }

    // Smooth auto-step camera offset
    if (Math.abs(this.stepUpOffset) > 0.001) {
      this.stepUpOffset *= Math.pow(0.00005, dt); // fast exponential decay
    } else {
      this.stepUpOffset = 0;
    }

    // Update camera
    this.camera.position.set(
      this.position.x,
      this.position.y + PLAYER_HEIGHT - 0.1 + this.stepUpOffset,
      this.position.z
    );

    const lookDir = new THREE.Vector3(
      -Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      -Math.cos(this.yaw) * Math.cos(this.pitch)
    );
    this.camera.lookAt(this.camera.position.clone().add(lookDir));
  }

  teleportTo(x: number, y: number, z: number, yaw: number) {
    this.position.set(x, y, z);
    this.velocity.set(0, 0, 0);
    this.yaw = yaw;
    this.portalCooldown = PORTAL_COOLDOWN;
    this.launching = false;
  }

  respawn(x: number, y: number, z: number) {
    this.dead = false;
    this.oxygen = MAX_OXYGEN;
    this.velocity.set(0, 0, 0);
    this.position.set(x, y, z);
  }

  getLookDirection(): THREE.Vector3 {
    return new THREE.Vector3(
      -Math.sin(this.yaw) * Math.cos(this.pitch),
      Math.sin(this.pitch),
      -Math.cos(this.yaw) * Math.cos(this.pitch)
    );
  }
}
